import { useAssetSidebar } from './[Asset].sidebar';
import { Box } from '@mui/material';
import { useFlag } from '@unleash/proxy-client-react';
import { UnitDetailTopbar } from 'components/layout/UnitDetailTopbar';
import { CHAT_DEFAULT_WIDTH } from 'features/assetControl/assetControl.constants';
import { shouldRenderOperatorChat } from 'features/assetControl/assetControl.utils';
import { OperatorChat } from 'features/assetControl/components/OperatorChat';
import { use404RedirectIf } from 'hooks/use404RedirectIf';
import useAuth from 'hooks/useAuth';
import { useAxiosQuery } from 'hooks/useAxiosQuery';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { useGQLQuery } from 'hooks/useGQLQuery';
import { useTimerange } from 'hooks/useTimerange';
import useUnitsConnectionStatus from 'hooks/useUnitsConnectionStatus';
import { sortBy } from 'lodash';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate, Outlet, generatePath, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { matchRoutes, useLocation } from 'react-router-dom';
import { routes } from 'routes';
import { GREENER_UNIT_KEY, READ_GREENER_UNIT } from 'services/gql/greenerUnits';
import { GreenerUnit, Project } from 'types';
import { DEVICE_TYPE_IDS } from 'utils/constants';
import { determineUnitAvailableDateRange } from 'utils/units';
import { PROJECT_CACHE_KEY } from 'services/queryKeys';

const getCurrentProject = (projectExternalIdentifier) => {
  const { data: project } = useAxiosQuery<Project>(
    PROJECT_CACHE_KEY(projectExternalIdentifier),
    `/projects/my-projects/?external_identifier__eq=${projectExternalIdentifier}`
  );

  return project?.[0];
};

const isAssetOwnedByUsersOrganisation = (asset, user) =>
  asset?.owningOrganisationId === user?.organisation.organisationId;

export const AssetLayout = () => {
  useAssetSidebar();
  const { unitExternalIdentifier } = useParams();
  const { units } = useOutletContext<{ units: GreenerUnit[] }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isChatEnabled = useFlag('chat');
  const { isMobile } = useBreakpoint();
  const [isChatSplit, setIsChatSplit] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  //@ts-expect-error check react router 6 for type mismatch
  const matchedRoutes = matchRoutes(routes, location);

  //@ts-expect-error gql will be retired
  const asset = useGQLQuery(GREENER_UNIT_KEY(unitExternalIdentifier), READ_GREENER_UNIT, {
    externalIdentifier: unitExternalIdentifier,
  }).data.greenerUnit;

  const isReportingOnlyAsset = asset?.watcherType?.deviceType?.deviceTypeId === DEVICE_TYPE_IDS.REPORTING_ONLY_BATTERY;
  const isReportsPage = location.pathname.includes('reports');
  const isReportsPageEnabled = useFlag('assets-reports');

  const shouldRedirectFromReportsPage = isReportsPage && (!isReportingOnlyAsset || !isReportsPageEnabled);
  const shouldRedirectFromNonReportsPage = !isReportsPage && isReportingOnlyAsset;

  if (shouldRedirectFromReportsPage || shouldRedirectFromNonReportsPage) {
    return <Navigate to="/" replace />;
  }

  use404RedirectIf(!asset);

  const isOnline = useUnitsConnectionStatus()?.[asset?.externalIdentifier] ?? false;

  const projectExternalIdentifier = asset?.latestProject?.externalIdentifier;
  const projectData = getCurrentProject(projectExternalIdentifier);

  const availableRange = determineUnitAvailableDateRange(
    projectData?.project,
    projectData?.projectGreenerUnits,
    unitExternalIdentifier
  );

  const { range, ...timerangeProps } = isAssetOwnedByUsersOrganisation(asset, user)
    ? useTimerange(undefined, 3)
    : useTimerange(availableRange, 3);

  const timerangePickerProps = {
    ...timerangeProps,
    ...(!isAssetOwnedByUsersOrganisation(asset, user) && { availableRange }),
  };

  function onChangeUnit(newSelectedUnit: GreenerUnit) {
    const matched = matchedRoutes?.find((m) => m.pathnameBase === location.pathname);
    const isNewAssetReportingOnly =
      newSelectedUnit?.deviceType?.deviceTypeId === DEVICE_TYPE_IDS.REPORTING_ONLY_BATTERY;

    if (matched) {
      let newPath = isNewAssetReportingOnly
        ? '/assets/:unitExternalIdentifier/reports'
        : '/assets/:unitExternalIdentifier';

      if (matched.pathnameBase.includes('basic-graphs') && !isNewAssetReportingOnly) {
        newPath = '/assets/:unitExternalIdentifier/basic-graphs';
      } else if (matched.pathnameBase.includes('advanced-graphs') && !isNewAssetReportingOnly) {
        newPath = '/assets/:unitExternalIdentifier/advanced-graphs';
      }

      navigate(
        generatePath(newPath, {
          unitExternalIdentifier: newSelectedUnit.externalIdentifier,
        })
      );
    }
  }

  const isChatVisible = shouldRenderOperatorChat(user?.role, asset, isChatEnabled);

  useEffect(() => {
    if (!isChatVisible) {
      setIsOpen(false);
      setIsChatSplit(false);
    }
  }, [asset, isChatEnabled, user?.role]);

  return asset ? (
    <>
      <Helmet key={location.pathname}>
        <title>{unitExternalIdentifier}</title>
      </Helmet>
      <Box
        sx={{
          transition: 'margin-right 0.3s',
          marginRight: !isMobile && isChatSplit ? `${CHAT_DEFAULT_WIDTH}px` : 0,
        }}
      >
        <UnitDetailTopbar
          unitSelectorProps={{
            units: sortBy(units, ['externalIdentifier']),
            onChange: onChangeUnit,
            selectedUnitExtId: unitExternalIdentifier,
          }}
          showRangePicker={location.pathname.includes('graphs')}
          timerangePickerProps={{
            range,
            ...timerangePickerProps,
          }}
        />
        <Outlet context={{ range, selectedAsset: asset, isOnline }} />
      </Box>

      {isChatVisible && (
        <OperatorChat
          unit={asset}
          isOnline={isOnline}
          isChatSplit={isChatSplit}
          setIsChatSplit={setIsChatSplit}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
        />
      )}
    </>
  ) : null;
};
